<script setup lang="ts">
import type { FinanceMetricKey } from '@metapic-apps/shared'
import { defaultFormatter, formatterProviderKey } from '@metapic-apps/shared'
import type { Component, ComputedRef } from 'vue'
import { computed, inject } from 'vue'

import { useAdvertiserMetrics, useFinanceMetrics, usePerformanceMetrics } from '@/api/performance.api'
import InstagramIcon from '@/assets/icons/instagram.svg?component'
import OtherIcon from '@/assets/icons/other-costs-icon.svg?component'
import SnapchatIcon from '@/assets/icons/snapchat-costs-icon.svg?component'
import TiktokIcon from '@/assets/icons/tiktok.svg?component'
import type { MetricBreakdownLine } from '@/components/MetricCard'
import { MetricCard } from '@/components/MetricCard'
import { formatChangePercent } from '@/lib/adapters/performance.adapter'
import { useAdvertiserPermissions } from '@/lib/hooks/advertiser-permissions.hooks'
import { usePerformanceFilter } from '@/lib/hooks/performance-filter.hooks'
import type { AdvertiserMetricKey, PerformanceMetric, SocialMetricKey } from '@/lib/types/performance.types'
const props = defineProps<{ creatorId?: number }>()
const { formatMoney, formatNumber } = inject(formatterProviderKey, defaultFormatter)
const { hideMedia, hideUsers, hideSales, hideCosts } = useAdvertiserPermissions()
type MetricFormat = 'number' | 'percent' | 'money'
type MetricVisibility = 'all' | 'overviewOnly' | 'creatorOnly'
type EstimatedRateConfig = {
  description: string
  threshold?: number
}
type MetricPermission = 'media' | 'users' | 'sales' | 'costs'
type MetricConfigBase = {
  label: string
  format: MetricFormat
  lowerIsBetter?: boolean
  visibility?: MetricVisibility
  infoTitle?: string
  description?: string
  emptyValue?: string
  estimatedRate?: EstimatedRateConfig
  hiddenBy?: MetricPermission[]
}
type MetricConfig =
  | (MetricConfigBase & { source: 'social'; key: SocialMetricKey })
  | (MetricConfigBase & { source: 'finance'; key: FinanceMetricKey })
  | (MetricConfigBase & { source: 'advertiser'; key: AdvertiserMetricKey })

const METRICS: MetricConfig[] = [
  {
    key: 'unique_views',
    label: 'Est. unique views',
    format: 'number',
    source: 'social',
    hiddenBy: ['media'],
    infoTitle: 'Estimated unique views',
    description: "Mix of direct data and estimated based on each creator's performance patterns.\nAlso known as ‘Reach’ by Instagram.",
    estimatedRate: {
      description:
        "{rate}% of this data comes directly from connected accounts. The rest is estimated based on each creator's performance patterns. Also known as ‘Reach’ by Instagram.",
    },
  },
  {
    key: 'views',
    label: 'Est. views',
    format: 'number',
    source: 'social',
    hiddenBy: ['media'],
    infoTitle: 'Estimated views',
    description: "Mix of direct data and estimated based on each creator's performance patterns.",
    estimatedRate: {
      description:
        "{rate}% of this data comes directly from connected accounts. The rest is estimated based on each creator's performance patterns.",
    },
  },
  {
    key: 'total_followers',
    label: 'Total followers',
    format: 'number',
    source: 'social',
    visibility: 'creatorOnly',
    hiddenBy: ['users'],
  },
  {
    key: 'media_value',
    label: 'Est. media value',
    format: 'money',
    source: 'social',
    emptyValue: '0',
    hiddenBy: ['media'],
    infoTitle: 'Estimated media value',
    description: "The estimated monetary worth of your content's exposure.",
  },
  { key: 'content_produced', label: 'Content produced', format: 'number', source: 'social', hiddenBy: ['media'] },
  {
    key: 'creators',
    label: 'Creators',
    format: 'number',
    source: 'advertiser',
    visibility: 'overviewOnly',
    hiddenBy: ['users'],
  },
  {
    key: 'new_creators_activated',
    label: 'New creators activated',
    format: 'number',
    source: 'advertiser',
    visibility: 'overviewOnly',
    hiddenBy: ['users'],
  },
  { key: 'clicks', label: 'Clicks', format: 'number', source: 'finance' },
  { key: 'number_of_orders', label: 'Orders', format: 'number', source: 'finance', hiddenBy: ['sales'] },
  { key: 'order_value', label: 'Order value', format: 'money', source: 'finance', emptyValue: '0', hiddenBy: ['sales'] },
  {
    key: 'avg_order_value',
    label: 'Average order value',
    format: 'money',
    source: 'finance',
    visibility: 'overviewOnly',
    emptyValue: '0',
    hiddenBy: ['sales'],
  },
  { key: 'revenue_potential', label: 'Est. total cost', format: 'money', source: 'finance', emptyValue: '0', hiddenBy: ['costs'] },
  {
    key: 'advertiser_roas',
    label: 'ROAS',
    format: 'number',
    source: 'finance',
    hiddenBy: ['sales', 'costs'],
    infoTitle: 'Return on Ad Spend',
    description: 'Revenue earned per money spent on advertising.',
  },
  {
    key: 'advertiser_ers',
    label: 'ERS',
    format: 'percent',
    lowerIsBetter: true,
    source: 'finance',
    hiddenBy: ['sales', 'costs'],
    infoTitle: 'Effective Revenue Share',
    description: 'The percentage of your total revenue consumed by cost.',
  },
]
const EMPTY_VALUE = '—'
const isVisible = (metric: MetricConfig): boolean => {
  const visibility = metric.visibility ?? 'all'
  if (visibility === 'creatorOnly') {
    return props.creatorId != null
  }
  if (visibility === 'overviewOnly') {
    return props.creatorId == null
  }
  return true
}
const permissionHidden: Record<MetricPermission, ComputedRef<boolean>> = {
  media: hideMedia,
  users: hideUsers,
  sales: hideSales,
  costs: hideCosts,
}
const isHiddenByPermission = (metric: MetricConfig): boolean =>
  (metric.hiddenBy ?? []).some((permission) => permissionHidden[permission].value)
const visibleMetrics = computed(() => METRICS.filter((metric) => isVisible(metric) && !isHiddenByPermission(metric)))
const visibleKeysBySource = computed(() => {
  const keys = {
    social: [] as SocialMetricKey[],
    finance: [] as FinanceMetricKey[],
    advertiser: [] as AdvertiserMetricKey[],
  }
  for (const metric of visibleMetrics.value) {
    switch (metric.source) {
      case 'social':
        keys.social.push(metric.key)
        break
      case 'finance':
        keys.finance.push(metric.key)
        break
      case 'advertiser':
        keys.advertiser.push(metric.key)
        break
    }
  }
  return keys
})
const socialKeys = computed(() => visibleKeysBySource.value.social)
const financeKeys = computed(() => visibleKeysBySource.value.finance)
const advertiserKeys = computed(() => visibleKeysBySource.value.advertiser)
const { metricsFilter } = usePerformanceFilter(() => props.creatorId)
const { metricsState: socialState } = usePerformanceMetrics(metricsFilter, socialKeys)
const { metricsState: financeState } = useFinanceMetrics(metricsFilter, financeKeys)
const { metricsState: advertiserState } = useAdvertiserMetrics(metricsFilter, advertiserKeys)
const stateForSource = {
  social: socialState,
  finance: financeState,
  advertiser: advertiserState,
} as const
const formatMetricValue = (metric: PerformanceMetric | undefined, format: MetricFormat, emptyValue: string = EMPTY_VALUE): string => {
  if (metric?.value == null) {
    return emptyValue
  }
  if (format === 'money') {
    const amount = typeof metric.value === 'number' ? metric.value : Number(metric.value.amount)
    if (amount === 0) {
      return emptyValue
    }
    return typeof metric.value === 'number'
      ? formatNumber(metric.value, 'standard', 'decimal', 2)
      : formatMoney(metric.value, { notation: 'compact', maximumFractionDigits: 1, minimumFractionDigits: 0 })
  }
  if (typeof metric.value !== 'number') {
    return EMPTY_VALUE
  }
  if (format === 'percent') {
    return `${formatNumber(metric.value * 100)}%`
  }
  return formatNumber(metric.value, 'compact')
}
const formatMetricLongValue = (metric: PerformanceMetric | undefined, format: MetricFormat): string | undefined => {
  if (metric?.value == null) {
    return undefined
  }
  if (format === 'money') {
    const amount = typeof metric.value === 'number' ? metric.value : Number(metric.value.amount)
    if (amount === 0) {
      return undefined
    }
    return typeof metric.value === 'number'
      ? formatNumber(metric.value, 'standard', 'decimal', 2)
      : formatMoney(metric.value, { maximumFractionDigits: 2 })
  }
  return format === 'number' && typeof metric.value === 'number' ? formatNumber(metric.value) : undefined
}
const SOURCE_ICON: Partial<Record<string, Component>> = {
  instagram: InstagramIcon,
  tiktok: TiktokIcon,
  snapchat: SnapchatIcon,
  other: OtherIcon,
}

const buildMetricBreakdown = (metric: PerformanceMetric | undefined): MetricBreakdownLine[] | undefined => {
  const metadata = metric?.metadata
  if (!metadata || !('breakdown' in metadata)) {
    return undefined
  }
  return metadata.breakdown.map((entry) => ({
    key: entry.key,
    icon: SOURCE_ICON[entry.key],
    value: `(${formatNumber(entry.views)} / 1,000) x ${formatMoney(entry.cpm, { allowZero: true })}`,
  }))
}

const DEFAULT_DIRECT_DATA_THRESHOLD = 0.3
const resolveDescription = (config: MetricConfig, metric: PerformanceMetric | undefined): string | undefined => {
  const metadata = metric?.metadata
  const rateConfig = config.estimatedRate
  if (!rateConfig || !metadata || !('estimatedRate' in metadata)) {
    return config.description
  }
  const directRate = 1 - metadata.estimatedRate
  if (directRate < (rateConfig.threshold ?? DEFAULT_DIRECT_DATA_THRESHOLD)) {
    return config.description
  }
  return rateConfig.description.replace('{rate}', formatNumber(directRate * 100))
}

const cards = computed(() =>
  visibleMetrics.value.map((config) => {
    const state = stateForSource[config.source].value[config.key]
    const metric = state?.metric
    const change = formatChangePercent(metric?.changePercent, config.lowerIsBetter)
    const value = formatMetricValue(metric, config.format, config.emptyValue)
    const longValue = formatMetricLongValue(metric, config.format)
    return {
      key: config.key,
      title: config.label,
      infoTitle: config.infoTitle,
      description: resolveDescription(config, metric),
      value,
      longValue: longValue === value ? undefined : longValue,
      additionalValue: change.additionalValue,
      trend: change.trend,
      loading: state?.isLoading ?? false,
      breakdown: buildMetricBreakdown(metric),
    }
  }),
)
</script>
<template>
  <div class="performance-metrics">
    <MetricCard
      v-for="card in cards"
      :key="card.key"
      :title="card.title"
      :info-title="card.infoTitle"
      :description="card.description"
      :value="card.value"
      :long-value="card.longValue"
      :additional-value="card.additionalValue"
      :trend="card.trend"
      :loading="card.loading"
      :breakdown="card.breakdown"
    />
  </div>
</template>
<style scoped lang="scss">
.performance-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
</style>
